import React, { FC, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, SelectOption, TranslatedString } from '../blocks/localization';

type CarrierSearchableSelectProps = {
    value: any;
    onChange?(value: string): void;
    selectOptions: { value: string; label: TranslatedString }[];
    dataTestId?: string;
    isDisabled?: boolean;
    placeholder?: TranslatedString;
    label?: TranslatedString;
    width?: number;
    isVisible?: boolean;
    limit?: number;
    minSearchChars?: number;
};

const CarrierSearchableSelect: FC<CarrierSearchableSelectProps> = ({
    value,
    onChange,
    selectOptions,
    dataTestId,
    isDisabled,
    placeholder,
    label,
    width,
    isVisible,
    limit = 10,
    minSearchChars = 1,
}) => {
    const { t } = useTranslation('enterTrackingInfoPage');
    const [query, setQuery] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const selectRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const normalizedQuery = query.trim().toLowerCase();
    let filteredOptions =
        normalizedQuery.length < minSearchChars
            ? []
            : selectOptions
                  .filter((option) => String(option.label).toLowerCase().includes(normalizedQuery))
                  .slice(0, limit);

    const selectedOption = selectOptions.find((opt) => opt.value === value);
    if (selectedOption && !filteredOptions.some((opt) => opt.value === selectedOption.value)) {
        filteredOptions = [...filteredOptions, selectedOption].slice(0, limit);
    }

    const handleChange = (nextValue: string) => {
        if (onChange) {
            onChange(nextValue);
        }
        setQuery('');
        setIsDropdownOpen(false);
    };

    const handleSearch = (searchQuery: string) => {
        setQuery(searchQuery);
        if (!isDropdownOpen) {
            setIsDropdownOpen(true);
        }
    };

    const handleClick = () => {
        setIsDropdownOpen(true);
    };

    const dynamicPlaceholder = isDropdownOpen && !query ? (t('typeToSearch-label') as TranslatedString) : placeholder;

    return (
        <>
            {(isVisible === undefined || isVisible) && (
                <div ref={selectRef} onClick={handleClick} role={'button'} tabIndex={0}>
                    <Select
                        value={value}
                        onChange={handleChange}
                        disabled={isDisabled}
                        label={label}
                        placeholder={dynamicPlaceholder}
                        searchQuery={query}
                        onSearch={handleSearch}
                        width={width}
                        data-testid={dataTestId}
                    >
                        {filteredOptions.map((option) => (
                            <SelectOption key={option.value} value={option.value} label={option.label} />
                        ))}
                    </Select>
                </div>
            )}
        </>
    );
};

export { CarrierSearchableSelect };
